import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Box, Alert, Divider, Select, MenuItem, FormControl, InputLabel, Card, CardContent, Chip, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { db } from '../utils/firebase';
import { collection, addDoc, getDocs, query } from 'firebase/firestore';

const AdminUserManagement = () => {
  const availableModules = React.useMemo(() => [
    { id: 'expense-splitter', name: 'Expense Splitter' },
    { id: 'trip-manager', name: 'Trip Manager' }
  ], []);

  // Helper functions for consistent formatting
  const formatName = (name) => {
    if (!name) return '';
    return name.toLowerCase().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatEmail = (email) => {
    if (!email) return '';
    return email.toLowerCase();
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    module: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [existingUsers, setExistingUsers] = useState([]);

  const fetchExistingUsers = React.useCallback(async () => {
    try {
      const allUsers = [];
      for (const module of availableModules) {
        const q = query(collection(db, `modules/${module.id}/users`));
        const querySnapshot = await getDocs(q);
        const moduleUsers = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          module: module.id,
          moduleName: module.name
        }));
        allUsers.push(...moduleUsers);
      }
      setExistingUsers(allUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [availableModules]);

  React.useEffect(() => {
    fetchExistingUsers();
  }, [fetchExistingUsers]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Name is required.';
    if (!formData.email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) return 'Please enter a valid email address.';
    if (!formData.module) return 'Module selection is required.';

    // Check if user already exists in the selected module
    const existingUser = existingUsers.find(user =>
      user.email.toLowerCase() === formData.email.trim().toLowerCase() &&
      user.module === formData.module
    );
    if (existingUser) return `User already exists in ${availableModules.find(m => m.id === formData.module)?.name} module.`;

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await addDoc(collection(db, `modules/${formData.module}/users`), {
        name: formData.name.trim(),
        email: formData.email.trim(),
        createdAt: new Date(),
        addedByAdmin: true
      });

      setFormData({ name: '', email: '', module: '' });
      setSubmitted(true);
      setError('');
      fetchExistingUsers(); // Refresh the list
    } catch (err) {
      console.error('Error adding user:', err);
      setError('Failed to add user. Please try again.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>User Management</Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Add users to specific modules (if users prefer admin assistance for registration).
      </Typography>
      <Divider sx={{ mb: 4 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {submitted && <Alert severity="success" sx={{ mb: 2 }}>User added successfully!</Alert>}

      {/* Add User Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Add New User</Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Module</InputLabel>
              <Select
                value={formData.module}
                onChange={(e) => handleChange('module', e.target.value)}
              >
                {availableModules.map((module) => (
                  <MenuItem key={module.id} value={module.id}>
                    {module.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button type="submit" variant="contained" color="primary" fullWidth>
              Add User
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Existing Users */}
      <Typography variant="h6" gutterBottom>Existing Users</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        View and manage all users registered in different modules. Users are grouped by module.
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {existingUsers.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
          No users registered yet. Add your first user above!
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          {availableModules.map((module) => {
            const moduleUsers = existingUsers.filter(user => user.module === module.id);
            if (moduleUsers.length === 0) return null;

            return (
              <Card key={module.id} elevation={1} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1b5e20', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {module.name}
                    <Chip
                      label={`${moduleUsers.length} user${moduleUsers.length !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        backgroundColor: 'primary.50',
                        fontWeight: 'medium',
                      }}
                    />
                  </Typography>
                  <Table sx={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                    <TableBody>
                      {moduleUsers.map((user, index) => (
                        <TableRow key={user.id} sx={{ border: 'none' }}>
                          <TableCell sx={{ border: 'none', padding: 0 }}>
                            <Box
                              sx={{
                                py: 2,
                                px: 1,
                                backgroundColor: 'white',
                                width: '100%',
                                borderBottom: '2px solid transparent',
                                borderImage: 'repeating-linear-gradient(90deg, #4A90E2 0px, #4A90E2 3px, transparent 3px, transparent 6px, #4A90E2 6px, #4A90E2 8px, transparent 8px, transparent 12px, #4A90E2 12px, #4A90E2 15px, transparent 15px, transparent 18px) 1',
                                mb: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                    {formatName(user.name)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {formatEmail(user.email)}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                  Added: {user.createdAt?.toDate?.()
                                    ? user.createdAt.toDate().toLocaleDateString()
                                    : new Date(user.createdAt).toLocaleDateString()
                                  }
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Container>
  );
};

export default AdminUserManagement;